import { EnvironmentConfig } from "./configs/environment.config";

// Based on https://dev.to/esponges/indexeddb-your-offline-and-serverless-db-in-your-browser-with-react-3hm7
// and updated to singleton cached instance to prevent react strict mode issues

let dbInstance: IDBDatabase | null = null; // Holds the singleton instance of the database
let version = EnvironmentConfig.IndexDBVersion;

export enum Stores {
    Auth = "auth",
    Users = "users",
    Errors = "errors",
}

export const initDB = (): Promise<IDBDatabase> => {
    if (dbInstance) {
        // Return the already initialized database instance
        return Promise.resolve(dbInstance);
    }

    return new Promise((resolve, reject) => {
        const request = indexedDB.open('thatsme_baby_db', version);

        request.onupgradeneeded = () => {
            const db = request.result;

            for (const key in Stores) {
                if (!Stores.hasOwnProperty(key)) {
                    continue;
                }

                const storeName = Stores[key as keyof typeof Stores];

                if(db.objectStoreNames.contains(storeName))
                {
                    db.deleteObjectStore(storeName);
                }

                switch (storeName){
                    case Stores.Auth:
                    case Stores.Users:
                    {
                        db.createObjectStore(storeName, { keyPath: 'id' });
                        break;
                    }
                    case Stores.Errors:
                    {
                        db.createObjectStore(storeName, { keyPath: 'type' });
                        break;
                    }
                }
            }
        };

        request.onsuccess = () => {
            dbInstance = request.result;
            resolve(dbInstance);
        };

        request.onerror = (event) => {
            console.error("Error opening the database:", event);
            reject(request.error);
        };
    });
};

export const addIndexedData = async <T>(storeName: string, data: T): Promise<T | string | null> => {
    try {
        const db = await initDB();
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        const response = store.add(data);

        return new Promise((resolve, reject) => {
            response.onsuccess = () => resolve(data);
            response.onerror = () => resolve(response.error ? response.error.message : 'Unknown error trying to add to store');
        });
    } catch (error) {
        console.error("Error adding data:", error);
        return 'Error initializing database';
    }
};

export const deleteIndexedData = async (storeName: string, key: string | number): Promise<boolean | string> => {
    try {
        const db = await initDB();
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        const response = store.delete(key);

        return new Promise((resolve, reject) => {
            response.onsuccess = () => resolve(true);
            response.onerror = () => resolve(response.error ? response.error.message : 'Unknown error deleting data');
        });
    } catch (error) {
        console.error("Error deleting data:", error);
        return 'Error initializing database';
    }
};

export const deleteAllIndexedData = async (storeName: string): Promise<boolean | string> => {
    try {
        const db = await initDB();
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        const response = store.clear();

        return new Promise((resolve, reject) => {
            response.onsuccess = () => resolve(true);
            response.onerror = () => resolve(response.error ? response.error.message : 'Unknown error deleting data');
        });
    } catch (error) {
        console.error("Error deleting data:", error);
        return 'Error initializing database';
    }
};

export const updateIndexedData = async <T>(storeName: string, key: string | number, data: T): Promise<T | string | null> => {
    try {
        const db = await initDB();
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        const getRequest = store.get(key);

        return new Promise((resolve, reject) => {
            getRequest.onsuccess = () => {
                if (getRequest.result) {
                    const updatedData = { ...getRequest.result, ...data };
                    const putRequest = store.put(updatedData);

                    putRequest.onsuccess = () => resolve(updatedData);
                    putRequest.onerror = () => resolve(putRequest.error ? putRequest.error.message : 'Error updating data');
                } else {
                    resolve(null);
                }
            };

            getRequest.onerror = () => resolve(getRequest.error ? getRequest.error.message : 'Error retrieving data for update');
        });
    } catch (error) {
        console.error("Error updating data:", error);
        return 'Error initializing database';
    }
};

export const getIndexedData = async <T>(storeName: Stores, key: string | number): Promise<T | string> => {
    try {
        const db = await initDB();
        const tx = db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        const response = store.get(key);

        return new Promise((resolve, reject) => {
            response.onsuccess = () => resolve(response.result);
            response.onerror = () => resolve(response.error ? response.error.message : 'Error retrieving data from store');
        });
    } catch (error) {
        console.error("Error getting store data:", error);
        return 'Error initializing database';
    }
};

export const getAllIndexedData = async <T>(storeName: Stores): Promise<T[] | string> => {
    try {
        const db = await initDB();
        const tx = db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        const response = store.getAll();

        return new Promise((resolve, reject) => {
            response.onsuccess = () => resolve(response.result);
            response.onerror = () => resolve(response.error ? response.error.message : 'Error retrieving data from store');
        });
    } catch (error) {
        console.error("Error getting store data:", error);
        return 'Error initializing database';
    }
};

export const checkIfDataExists = async (storeName : Stores, key: string | number): Promise<boolean | string> => {
    try {
        const db = await initDB();
        const tx = db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);

        // Use the `get` method to retrieve the record by key
        const response = store.get(key);

        return new Promise((resolve, reject) => {
            response.onsuccess = () => {
                if (response.result !== undefined) {
                    // Record exists
                    resolve(true);
                } else {
                    // Record does not exist
                    resolve(false);
                }
            };
            response.onerror = () => resolve(response.error ? response.error.message : 'Error retrieving data from store');
        });
    } catch (error) {
        console.error("Error getting store data:", error);
        return 'Error initializing database';
    }
}
